/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useEffect, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { useHeadings } from '@af/design-system-docs-ui';
import Heading from '@atlaskit/heading';
import { Box, Text, xcss } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import { N30 } from '@atlaskit/theme/colors';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';

import { internalAnchorOffset } from '../../utilities/constants';

const navStyles = css({
	minWidth: '11.5rem',
	position: 'sticky',
	borderInlineStart: `1px solid ${token('color.border', N30)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	insetBlockStart: internalAnchorOffset,
	marginBlockEnd: token('space.400', '32px'),
	paddingBlock: token('space.050', '4px'),
	paddingInline: token('space.150', '12px'),
});

const unselectedAnchorStyles = xcss({
	textDecoration: 'none',
	color: 'color.text.subtlest',
	':hover': {
		color: 'color.text.subtle',
		textDecoration: 'underline',
	},
	':active': {
		color: 'color.text',
	},
});

const selectedAnchorStyles = xcss({
	textDecoration: 'none',
	color: 'color.text.selected',

	'::before': {
		content: "''",
		width: '1px',
		height: 'calc(100% + 8px)',
		position: 'absolute',
		insetInlineStart: '-13px',
		insetBlockStart: '-5px',
		borderInlineStartColor: 'color.border.selected',
		borderInlineStartWidth: 'border.width',
		borderInlineStartStyle: 'solid',
	},
	':hover': {
		textDecoration: 'none',
	},
});

const listItemStyles = xcss({
	marginBlockEnd: 'space.0',
	marginBlockStart: 'space.150',
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NavList = styled.ul({
	listStyleType: 'none',
	padding: token('space.0', '0px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body.small', fontFallback.body.small),
});

/**
 * __Local nav__
 */
export const LocalNav = () => {
	const { headings } = useHeadings();
	const [activeHeading, setActiveHeading] = useState<string | null>(null);

	useEffect(() => {
		// this is a live list so it will keep track of all currently intersecting headings
		const selectedHeadings = document.getElementsByClassName('intersecting');

		const options = {
			// root margin set to 5px to pick up the entire page's subheadings
			rootMargin: `-${internalAnchorOffset}px 0px -55% 0px`,
			threshold: 0,
		};

		const callback = (entries: IntersectionObserverEntry[]) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					entry.target.classList.add('intersecting');
				} else {
					entry.target.classList.remove('intersecting');
				}
			});
			// set the lowest heading on the page to active
			if (selectedHeadings.length > 0) {
				const firstHeading = selectedHeadings[0].getAttribute('id');
				setActiveHeading(firstHeading);
			}
		};

		const observer = new IntersectionObserver(callback, options);

		if (headings) {
			headings.forEach((heading) => {
				const headingEl = document.getElementById(heading.id);
				if (headingEl) {
					observer.observe(headingEl);
				}
			});
		}

		return () => {
			observer.disconnect();
		};
		// ignored eslint here because if we add a dep on activeHeading it will cause an infinite loop when we updated state
	}, [headings]);

	return (
		<Fragment>
			{headings?.length > 0 && (
				<nav css={navStyles} aria-label="Page content">
					<Heading size="xxsmall" as="h3">
						Contents
					</Heading>
					<NavList>
						{headings.map((heading, index) => {
							const isCurrentHeading = activeHeading === heading.id;

							return (
								<Box
									as="li"
									key={`${heading.id}-${heading.depth}-${index}`}
									paddingInlineStart={heading.depth === 3 ? 'space.200' : undefined}
									xcss={listItemStyles}
								>
									<Text size="UNSAFE_small" weight="medium" color="color.text.subtlest">
										<Anchor
											href={`#${heading.id}`}
											onClick={() => {
												setActiveHeading(heading.id);
											}}
											xcss={isCurrentHeading ? selectedAnchorStyles : unselectedAnchorStyles}
											aria-current={isCurrentHeading ? true : undefined}
										>
											{heading.value}
										</Anchor>
									</Text>
								</Box>
							);
						})}
					</NavList>
				</nav>
			)}
		</Fragment>
	);
};

export default LocalNav;
